import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {Link} from 'react-router';
import moment from 'moment-timezone';

import systemTimeOffsetStore from 'client/generic/SystemTimeOffset/SystemTimeOffset.store';
import {getModelForResourceType} from 'resources/modelRegistry';

import {Card, Text} from '@albert-io/atomic';

import './assignment-card.scss';

function getDueDateBlurb(assignment, idsToSelect) {
  const dueDate = moment(assignment.getDueDateFromSettings(idsToSelect)).tz(moment.tz.guess());
  const now = moment(systemTimeOffsetStore.getCurrentTime()).tz(moment.tz.guess());
  if (assignment.isPastDue(idsToSelect)) {
    if (now.diff(dueDate, 'hours') < 1) {
      return 'Due less than an hour ago';
    }
    if (now.diff(dueDate, 'hours') < 2) {
      return 'Due 1 hour ago';
    }
    if (now.diff(dueDate, 'day') < 1) {
      return `Due ${now.diff(dueDate, 'hours')} hours ago`;
    }
    return `Due ${now.to(dueDate)}`;
  }

  if (assignment.isDueIn1Day(idsToSelect)) {
    return `Due ${now.to(dueDate)}`;
  }

  // Use Moment.js's customization feature to format this the way Design wants (@see https://momentjs.com/docs/#/customization/calendar/)
  const readableDueDate = dueDate.calendar(now, {
    nextDay: '[tomorrow at] h:mma z',
    nextWeek: 'dddd [at]  h:mma z',
    sameElse: 'MMMM D, YYYY [at] h:mma z'
  });

  // Fix bug in which MomentJS calendar() function capitalizes "tomorrow" (@see https://github.com/albert-io/project-management/issues/5149)

  return `Due ${readableDueDate}`;
}

const AssignmentCard = ({assignment, className, studentId, ...rest}) => {
  const id = assignment.getId();
  const name = assignment.getName();
  const classroom = assignment.getClassroomWithLatestAssignmentDueDate();
  const idsToSelect = {
    studentId,
    classroomId: classroom?.getId?.()
  };
  // Fairly confident we will never use the assessment route after migration but this is to be safe for now
  const slug = assignment.getSections().isEmpty()
    ? `/assignment/${assignment.getId()}`
    : `/assessments/${assignment.getId()}`;
  const isUrgent = assignment.isPastDue(idsToSelect) || assignment.isDueIn(1, 'hour', idsToSelect);
  return (
    <Card
      as={Link}
      aria-labelledby={id}
      border='darker'
      className={classnames('assignment-card u-text-decoration_none u-display_block', className)}
      paddingLevel={2}
      to={slug}
      {...rest}
    >
      <Text
        as='h4'
        className='assignment-card__title u-display_block u-mar-b_1 u-text-overflow_ellipsis u-overflow_hidden'
        bold
        emphasis='high'
        id={id}
      >
        {name}
      </Text>
      <Text
        as='div'
        bold={isUrgent}
        emphasis={isUrgent ? 'high' : 'low'}
        className='u-white-space_normal'
        color={isUrgent ? 'bright-orange' : 'black'}
        size='s'
      >
        {getDueDateBlurb(assignment, idsToSelect)}
      </Text>
    </Card>
  );
};

AssignmentCard.propTypes = {
  assignment: PropTypes.instanceOf(getModelForResourceType('assignment_v3')),
  className: PropTypes.string,
  studentId: PropTypes.string
};

export default AssignmentCard;
